import axios from "axios";
import { errorHandler } from "@/Services/errorHandler";
import queryStringify from "qs-stringify";

// Canonical DB table name for Kitchen & Table Logs (ticket #26214).
// Kept as a runtime string so production bundles retain the source identity.
const WAITER_TABLE_LOG = "waiter_table_log";

export default {
  state: {
    waiterReports: [],
  },
  getters: {
    getWaiterReports: (state) => state.waiterReports,
  },
  mutations: {
    setWaiterReports(state, payload) {
      state.waiterReports = payload;
      localStorage.setItem("waiterReports", JSON.stringify(payload));
    },
  },
  actions: {
    getWaiterData: async (context, data) => {
      let route = data.route;
      let query = data.query;
      let payload = {
        ...query,
        ...context.getters.getTimeFrame,
      };
      let stores = JSON.parse(localStorage.getItem(`stores`));
      let store = JSON.parse(localStorage.getItem(`selected_store`));

      if (stores.length === 0) return;
      let group = stores[0].Group;
      let storeDB = store.StoreDB;

      let headers = {
        group: window.btoa(group),
        DB: storeDB !== null ? window.btoa(storeDB) : null,
      };

      const queryString = `?` + queryStringify(payload);
      return axios
        .get(reportsApi + `${route}` + queryString, {
          headers,
        })
        .then(({ data }) => data)
        .catch((err) => errorHandler.tomcatError(err));
    },

    /**
     * Fetch kitchen/table audit rows from waiter/table-log with an explicit
     * date window. Used by the Kitchen & Table Logs surface (ticket #26214).
     * Does NOT merge the global timeline — the audit screen owns its presets.
     * Approach A: server returns date-bounded rows; search/action filters run client-side.
     * @param {object} _context - Vuex action context (unused; headers from localStorage).
     * @param {{ from: string, to: string, user?: string, mainTableName?: string, action?: string }} params
     * @returns {Promise<Array>} Raw waiter_table_log rows (most recent first).
     * @see docs/product-specs/2026-08-06-ticket-26214-kitchen-table-logs-refinement-spec.md §3
     */
    getTableLog: async (_context, params) => {
      const stores = JSON.parse(localStorage.getItem(`stores`));
      const store = JSON.parse(localStorage.getItem(`selected_store`));
      if (!stores || stores.length === 0) return [];

      const group = stores[0].Group;
      const storeDB = store && store.StoreDB != null ? store.StoreDB : null;
      const headers = {
        group: window.btoa(group),
        DB: storeDB !== null ? window.btoa(storeDB) : null,
      };

      // Only send defined filter keys — empty strings must not become exact-match filters.
      const payload = {};
      if (params && params.from) payload.from = params.from;
      if (params && params.to) payload.to = params.to;
      if (params && params.user) payload.user = params.user;
      if (params && params.mainTableName) payload.mainTableName = params.mainTableName;
      if (params && params.action) payload.action = params.action;

      const queryString = `?` + queryStringify(payload);
      return axios
        .get(reportsApi + `waiter/table-log` + queryString, { headers })
        .then(({ data }) => {
          // Backend may return a JSON string or an already-parsed array.
          let rows = [];
          if (typeof data === "string") {
            try {
              rows = JSON.parse(data);
            } catch (_e) {
              rows = [];
            }
          } else {
            rows = Array.isArray(data) ? data : [];
          }
          // Non-enumerable source tag — does not affect row rendering/export shape.
          if (Array.isArray(rows)) {
            Object.defineProperty(rows, "source", {
              value: WAITER_TABLE_LOG,
              enumerable: false,
            });
          }
          return rows;
        })
        .catch((err) => {
          errorHandler.tomcatError(err);
          throw err;
        });
    },

    // Printing functions for waiter reports
    async printWaiterReportPDF(context, fileData) {
      let route = fileData.route;
      let fileName = fileData.fileName;
      let payload = {
        ...context.getters.getTimeFrame,
      };

      let stores = JSON.parse(localStorage.getItem("stores"));
      if (!stores || stores.length === 0) return;

      let group = stores[0].Group;

      const queryString = `?` + queryStringify(payload);
      let headers = {
        group: window.btoa(group),
      };

      try {
        const response = await axios.get(docsApi + `${route}` + queryString, {
          headers,
          responseType: `blob`,
        });
        openFile(response, fileName);
      } catch (err) {
        errorHandler.tomcatError(err);
      }
    },

    async exportWaiterReportToExcel(context, fileData) {
      let route = fileData.route;
      let fileName = fileData.fileName;
      let payload = {
        ...context.getters.getTimeFrame,
      };

      let stores = JSON.parse(localStorage.getItem("stores"));
      if (!stores || stores.length === 0) return;
      let group = stores[0].Group;

      const queryString = `?` + queryStringify(payload);
      let headers = {
        group: window.btoa(group),
      };

      try {
        const response = await axios.get(docsApi + `${route}` + queryString, {
          headers,
          responseType: `blob`,
        });
        openFile(response, fileName);
      } catch (err) {
        errorHandler.tomcatError(err);
      }
    },
  },
};